import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Linkedin, Calendar, ArrowRight, ExternalLink, Globe, Volume2, VolumeX, Play } from 'lucide-react';
import Logo from './Logo';

const PortfolioIntroPage = () => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);

  // Check if video has been played in this session
  useEffect(() => {
    const played = sessionStorage.getItem('introVideoPlayed');
    if (played) {
      setHasPlayed(true);
    }
  }, []);

  // Auto-play video on mount
  useEffect(() => {
    if (videoRef.current && !hasPlayed) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            setShowPlayButton(false);
          })
          .catch(() => {
            // Autoplay was prevented, show play button
            setShowPlayButton(true);
          });
      }
    }
  }, [hasPlayed]);

  // Handle play button click
  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
        setShowPlayButton(false);
      });
    }
  };

  // Handle video end - mark as played
  const handleVideoEnded = () => {
    sessionStorage.setItem('introVideoPlayed', 'true');
    setHasPlayed(true);
    setIsPlaying(false);
  };

  // Handle video play
  const handleVideoPlay = () => {
    setIsPlaying(true);
    setShowPlayButton(false);
  };

  // Handle video pause
  const handleVideoPause = () => {
    setIsPlaying(false);
  };

  // Prevent video download via context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  // Toggle mute
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const portfolioContent = t.portfolioIntro || {
    hero: {
      title: "Introduction by Gonzalo Duranona",
      subtitle: "Technical architecture, mobile engineering leadership, and delivery of scalable products.",
      ctaPrimary: "Contact Gonzalo",
      ctaSecondary: "Visit company site",
      privateNote: "Private link."
    },
    whatWeDo: {
      title: "Engineering leadership and hands-on delivery",
      description: "Interware helps teams design and ship reliable mobile and cloud systems. We combine pragmatic architecture with strong execution: security, performance, clean delivery processes, and product-ready engineering."
    },
    focusAreas: {
      title: "Focus areas",
      items: [
        "Mobile architecture (iOS/Android native, Swift/SwiftUI, Kotlin, React Native)",
        "Backend & cloud (APIs, microservices, AWS/GCP/Azure)",
        "Security & compliance (hardening, attestation, certificate pinning strategies)",
        "Delivery acceleration (CI/CD, release automation, observability)",
        "Product engineering (from prototype to production)"
      ]
    },
    projects: {
      title: "Recent projects",
      items: [
        {
          title: "Mobile Banking Delivery",
          description: "Offline resilience, error handling strategy, security hardening, scalable release workflows."
        },
        {
          title: "Enterprise Mobile Platforms",
          description: "Architecture patterns, modularization, performance tuning, and long-term maintainability."
        },
        {
          title: "Cloud Services & APIs",
          description: "Secure APIs, infrastructure automation, and operational visibility."
        },
        {
          title: "AI-enabled Product Features",
          description: "Applied AI for content generation and product workflows, with responsible controls."
        }
      ]
    },
    workingStyle: {
      title: "Direct, clear, and execution-driven",
      description: "I work closely with stakeholders and engineering teams, translating business goals into concrete architecture and delivery plans. I'm comfortable leading teams, unblocking execution, and owning outcomes end-to-end."
    },
    contact: {
      title: "Let's connect",
      description: "If you'd like to discuss a project, a leadership role, or a collaboration, reach out.",
      email: "gonzalo@interware.dev",
      linkedin: "www.linkedin.com/in/gonzalodd",
      calendar: "calendly.com/duranona-gonzalo"
    },
    footer: "© Interware — Private portfolio link. Not for public distribution."
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* No index meta tag */}
      <meta name="robots" content="noindex, nofollow" />
      
      {/* Hero Section - Simplified Layout */}
      <section className="relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Logo size="default" />
          </div>
          
          {/* Title */}
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extralight text-white text-center mb-10">
            {portfolioContent.hero.title}
          </h1>
          
          {/* Video */}
          <div className="relative aspect-video bg-zinc-900 rounded-lg overflow-hidden shadow-2xl mb-10">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted={isMuted}
              playsInline
              controls={hasPlayed}
              controlsList="nodownload"
              onContextMenu={handleContextMenu}
              onEnded={handleVideoEnded}
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
              preload="auto"
            >
              <source src="/assets/introduction.mp4" type="video/mp4" />
            </video>
            
            {/* Play button - shown when autoplay fails */}
            {showPlayButton && !isPlaying && !hasPlayed && (
              <button
                onClick={handlePlayClick}
                className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors cursor-pointer"
                data-testid="video-play-button"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full transition-all duration-300 border border-white/30">
                  <Play className="w-8 h-8 text-white ml-1" fill="white" />
                </div>
              </button>
            )}
            
            {/* Mute/Unmute button - shown while video is playing and not ended */}
            {!hasPlayed && (
              <button
                onClick={toggleMute}
                className="absolute bottom-4 right-4 z-10 w-12 h-12 flex items-center justify-center bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full transition-all duration-300 border border-white/20"
                data-testid="video-mute-toggle"
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5 text-white" />
                ) : (
                  <Volume2 className="w-5 h-5 text-white" />
                )}
              </button>
            )}
          </div>
          
          {/* Subtitle / Description */}
          <p className="text-lg lg:text-xl text-zinc-400 font-light text-center mb-10 max-w-2xl mx-auto">
            {portfolioContent.hero.subtitle}
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
            <a 
              href={`mailto:${portfolioContent.contact.email}`}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black font-medium tracking-wide hover:bg-zinc-200 transition-colors"
            >
              <Mail className="w-4 h-4" />
              {portfolioContent.hero.ctaPrimary}
            </a>
            <a 
              href="/"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-zinc-700 text-white hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-all"
            >
              <Globe className="w-4 h-4" />
              {portfolioContent.hero.ctaSecondary}
            </a>
          </div>
          
          {/* Private note */}
          <p className="text-xs text-zinc-600 tracking-wide text-center pb-16">
            {portfolioContent.hero.privateNote}
          </p>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 lg:py-32 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-cyan-400 to-transparent" />
              <span className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-medium">About</span>
            </div>
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-extralight text-white mb-8">
              {portfolioContent.whatWeDo.title}
            </h2>
            <p className="text-lg text-zinc-400 font-light leading-relaxed">
              {portfolioContent.whatWeDo.description}
            </p>
          </div>
        </div>
      </section>

      {/* Focus Areas Section */}
      <section className="py-20 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-12 h-px bg-gradient-to-r from-cyan-400 to-transparent" />
            <span className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-medium">Expertise</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-extralight text-white mb-12">
            {portfolioContent.focusAreas.title}
          </h2>
          <div className="space-y-4">
            {portfolioContent.focusAreas.items.map((item, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-6 bg-zinc-950 border border-zinc-900 hover:border-zinc-800 transition-colors"
              >
                <ArrowRight className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                <p className="text-zinc-300 font-light">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section className="py-20 lg:py-32 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-12 h-px bg-gradient-to-r from-cyan-400 to-transparent" />
            <span className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-medium">Work</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-extralight text-white mb-12">
            {portfolioContent.projects.title}
          </h2>
          <div className="grid md:grid-cols-2 gap-1">
            {portfolioContent.projects.items.map((project, index) => (
              <div 
                key={index}
                className="group p-8 lg:p-10 bg-black border border-zinc-900 hover:border-zinc-800 transition-colors"
              >
                <h3 className="text-xl lg:text-2xl font-light text-white mb-4 group-hover:text-cyan-400 transition-colors">
                  {project.title}
                </h3>
                <p className="text-zinc-500 font-light leading-relaxed">
                  {project.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Working Style Section */}
      <section className="py-20 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-cyan-400 to-transparent" />
              <span className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-medium">Approach</span>
            </div>
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-extralight text-white mb-8">
              {portfolioContent.workingStyle.title}
            </h2>
            <p className="text-lg text-zinc-400 font-light leading-relaxed">
              {portfolioContent.workingStyle.description}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-32 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-cyan-400 to-transparent" />
              <span className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-medium">Contact</span>
            </div>
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-extralight text-white mb-8">
              {portfolioContent.contact.title}
            </h2>
            <p className="text-lg text-zinc-400 font-light leading-relaxed mb-12">
              {portfolioContent.contact.description}
            </p>
            
            {/* Contact Links */}
            <div className="space-y-4">
              <a 
                href={`mailto:${portfolioContent.contact.email}`}
                className="group flex items-center gap-4 p-6 bg-black border border-zinc-900 hover:border-cyan-500/50 transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-zinc-800 group-hover:border-cyan-500/50 transition-colors">
                  <Mail className="w-5 h-5 text-zinc-500 group-hover:text-cyan-400 transition-colors" />
                </div>
                <div>
                  <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Email</p>
                  <p className="text-white group-hover:text-cyan-400 transition-colors">{portfolioContent.contact.email}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-zinc-700 ml-auto group-hover:text-cyan-400 transition-colors" />
              </a>
              
              <a 
                href={`https://${portfolioContent.contact.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 p-6 bg-black border border-zinc-900 hover:border-cyan-500/50 transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-zinc-800 group-hover:border-cyan-500/50 transition-colors">
                  <Linkedin className="w-5 h-5 text-zinc-500 group-hover:text-cyan-400 transition-colors" />
                </div>
                <div>
                  <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">LinkedIn</p>
                  <p className="text-white group-hover:text-cyan-400 transition-colors">{portfolioContent.contact.linkedin}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-zinc-700 ml-auto group-hover:text-cyan-400 transition-colors" />
              </a>
              
              <a 
                href={`https://${portfolioContent.contact.calendar}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 p-6 bg-black border border-zinc-900 hover:border-cyan-500/50 transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-zinc-800 group-hover:border-cyan-500/50 transition-colors">
                  <Calendar className="w-5 h-5 text-zinc-500 group-hover:text-cyan-400 transition-colors" />
                </div>
                <div>
                  <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Schedule a call</p>
                  <p className="text-white group-hover:text-cyan-400 transition-colors">{portfolioContent.contact.calendar}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-zinc-700 ml-auto group-hover:text-cyan-400 transition-colors" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-black border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs text-zinc-600 tracking-wide">
            {portfolioContent.footer}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PortfolioIntroPage;
